import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, ChangeDetectorRef, Component, Input, OnInit } from '@angular/core';
import {
  BudgetRevenueAdminFormDto,
  BudgetRevenueAdminFormInterface
} from '@zaoblako/shared/modules/catalog/dtos/budget-revenue-admin/budget-revenue-admin-form.dto';
import { BudgetRevenueAdminFindInterface } from '@zaoblako/shared/modules/catalog/dtos/budget-revenue-admin/budget-revenue-admin-find.dto';
import { BasisBudgetRevenueAdminService } from '@zaoblako/web/core/services/basis/basis-budget-revenue-admin.service';
import { ConfirmDialogService } from '@zaoblako/web/core/services/confirm-dialog.service';
import { ErrorService } from '@zaoblako/web/core/services/error.service';
import { FormStoreService } from '@zaoblako/web/shared/components/form-sidebar/services/form-store.service';
import { FormType } from '@zaoblako/web/shared/components/form-sidebar/models/form-state';
import { BaseFormComponent } from '@zaoblako/web/shared/dumbs/base-form/base-form.component';
import { InputTextComponent } from '@zaoblako/web/shared/dumbs/input-text/input-text.component';
import { SpinnerComponent } from '@zaoblako/web/shared/dumbs/spinner/spinner.component';
import { JoinPipe } from '@zaoblako/web/shared/pipes/join.pipe';
import { ButtonComponent } from '@zaoblako/web/shared/dumbs/button/button.component';
import { CardModule } from 'primeng/card';
import { TagModule } from 'primeng/tag';
import { Observable } from 'rxjs';

@Component({
  standalone: true,
  selector: 'crm-revenue-admin-form',
  templateUrl: './revenue-admin-form.component.html',
  imports: [CommonModule, CardModule, InputTextComponent, JoinPipe, ButtonComponent, TagModule, SpinnerComponent],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class RevenueAdminFormComponent extends BaseFormComponent<BudgetRevenueAdminFormInterface> implements OnInit {
  @Input() public readonly index: number;
  public admin = <BudgetRevenueAdminFormInterface>{};
  public loading$: Observable<boolean>;

  public dto = BudgetRevenueAdminFormDto;
  public form$: Observable<FormType>;

  public constructor(
    private readonly basisBudgetRevenueAdminService: BasisBudgetRevenueAdminService,
    private readonly confirmService: ConfirmDialogService,
    public readonly formStoreService: FormStoreService,
    private readonly errorService: ErrorService,
    private readonly cdRef: ChangeDetectorRef
  ) {
    super();
  }

  public ngOnInit(): void {
    this.loading$ = this.formStoreService.select((state) => state.forms[this.index]?.loading);
    this.form$ = this.formStoreService.select((state) => state.forms[this.index]);

    if (this.formStoreService.getState.forms[this.index]?.isEdit) {
      this.getById();
    }
  }

  public getById(): void {
    this.formStoreService.setLoading(true, this.index);

    this.basisBudgetRevenueAdminService
      .findById<BudgetRevenueAdminFindInterface>(this.formStoreService.getState.forms[this.index]?.entityId, {
        withDeleted: true
      })
      .subscribe({
        next: (admin) => {
          this.admin = admin;
          this.formStoreService.setLoading(false, this.index);
          this.cdRef.markForCheck();
        }
      });
  }

  public saveAdmin(): void {
    if (this.checkValidate(this.admin).valid) {
      if (this.formStoreService.getState.forms[this.index]?.entityId) {
        this.formStoreService.setLoading(true, this.index);

        this.basisBudgetRevenueAdminService
          .update<BudgetRevenueAdminFormInterface, BudgetRevenueAdminFindInterface>(this.admin)
          .subscribe({
            next: (admin) => {
              this.formStoreService.setLoading(false, this.index);
              this.errorService.addSuccessMessage(`Администратор платежа "${this.admin.name}" обновлен!`);
              this.formStoreService.saveNext(admin, this.index);
            },
            error: () => {
              this.formStoreService.setLoading(false, this.index);
            }
          });
      } else {
        this.formStoreService.setLoading(true, this.index);

        this.basisBudgetRevenueAdminService.create<BudgetRevenueAdminFindInterface>(this.admin).subscribe({
          next: (admin) => {
            this.formStoreService.setLoading(false, this.index);
            this.errorService.addSuccessMessage('Администратор платежа создан!');
            this.formStoreService.saveNext(admin, this.index);
          },
          error: () => {
            this.formStoreService.setLoading(false, this.index);
          }
        });
      }
    }
  }

  public deleteAdmin(): void {
    this.confirmService.confirm({
      message: `Вы действительно хотите удалить администратора платежа "${this.admin.name}"?`,
      accept: () => {
        this.formStoreService.setLoading(true, this.index);
        this.cdRef.markForCheck();

        this.basisBudgetRevenueAdminService
          .deleteById(this.formStoreService.getState.forms[this.index]?.entityId)
          .subscribe({
            next: () => {
              this.errorService.addSuccessMessage(`Администратор платежа "${this.admin.name}" удален!`);
              this.formStoreService.deleteNext(this.index);
            },
            error: () => {
              this.formStoreService.setLoading(false, this.index);
            }
          });
      }
    });
  }

  public restoreAdmin(): void {
    this.confirmService.confirm({
      message: `Вы действительно хотите восстановить администратора платежа "${this.admin?.name}"?`,
      accept: () => {
        this.formStoreService.setLoading(true, this.index);
        this.cdRef.markForCheck();

        this.basisBudgetRevenueAdminService
          .restore(this.formStoreService.getState.forms[this.index]?.entityId)
          .subscribe({
            next: () => {
              this.admin = { ...this.admin, deleted: null } as BudgetRevenueAdminFindInterface;
              this.cdRef.markForCheck();
              this.errorService.addSuccessMessage(`Администратор платежа "${this.admin?.name}" восстановлен!`);
              this.formStoreService.saveNext(this.admin, this.index);
            },
            error: () => {
              this.formStoreService.setLoading(false, this.index);
            }
          });
      }
    });
  }
}
